import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, OperatorFunction, pipe } from 'rxjs';
import { debounceTime, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  CellEditorStateFacade,
  CODE_COMPLETE_DEBOUNCE_TIME,
  CodeCompletionContext,
  CodeCompletionFacade,
  CodeCompletionResponse,
  CodeCompletionSuggestion,
  EditorContext,
  NotebookTypes,
} from '@mode/notebooks/contract';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AiGatewayClientService } from '@mode/queries/ai-assist/domain';
import { AccountFacade } from '@mode/accounts/contract';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ReportFacade } from '@mode/reports/domain';
import { NotebookStateService } from '../notebook-state.service';

@Injectable()
export class CodeCompletionService implements CodeCompletionFacade {
  private _cellEditorTextChange = new BehaviorSubject<void>(undefined);
  private _codeCompletionSuggestion = new BehaviorSubject<CodeCompletionSuggestion | null>(null);
  private _codeCompletionContext = new BehaviorSubject<CodeCompletionContext | null>(null);

  constructor(
    private aiGatewayClientService: AiGatewayClientService,
    private accountFacade: AccountFacade,
    private reportFacade: ReportFacade,
    private cellEditorStateFacade: CellEditorStateFacade,
    private notebookStateService: NotebookStateService
  ) {}

  onCellEditorChange(): void {
    this._cellEditorTextChange.next();
  }

  fetchCodeCompletion(prompt: string, suffix: string | null): Observable<CodeCompletionResponse> {
    const currentSuggestion = this._codeCompletionSuggestion.value;
    const currentContext = this._codeCompletionContext.value;

    if (currentSuggestion && currentContext) {
      const { cellPrefix, suggestion } = currentSuggestion;
      const currentLinePrefix = currentContext.currentLinePrefix;
      const snippet = `${cellPrefix}${suggestion}`;
      if (snippet.startsWith(currentLinePrefix)) {
        const delta = snippet.substring(currentLinePrefix.length);
        const newCodeCompletionResponse: CodeCompletionResponse = {
          renderedCode: [delta],
          requestId: currentSuggestion.suggestionId,
        };
        return of(newCodeCompletionResponse);
      }
    }

    return combineLatest([this.accountFacade.currentOrg$, this.reportFacade.headerReportToken$]).pipe(
      switchMap(([organization, reportToken]) => {
        return this.aiGatewayClientService.getCodeCompletion({
          query: prompt,
          suffix,
          metadata: {
            reportToken,
            organizationToken: organization.token,
            organizationHandle: organization.username,
            planCode: organization.planCode,
          },
        });
      })
    );
  }

  submitFeedback(accepted: boolean, message?: string, rating?: number) {
    const currentSuggestion = this._codeCompletionSuggestion.value;
    if (currentSuggestion?.suggestionId) {
      const requestId = currentSuggestion.suggestionId;
      return combineLatest([this.accountFacade.currentOrg$, this.reportFacade.headerReportToken$]).pipe(
        switchMap(([organization, reportToken]) => {
          return this.aiGatewayClientService.submitCodeCompletionFeedback({
            accepted,
            requestId,
            message,
            rating,
            metadata: {
              reportToken,
              organizationToken: organization.token,
              organizationHandle: organization.username,
              planCode: organization.planCode,
            },
          });
        })
      );
    }

    return of({});
  }

  acceptCodeCompletionSuggestion(): void {
    this.submitFeedback(true).subscribe();
    this.clearCodeCompletionSuggestion();
  }
  rejectCodeCompletionSuggestion(): void {
    this.submitFeedback(false).subscribe();
    this.clearCodeCompletionSuggestion();
  }

  clearCodeCompletionSuggestion(): void {
    this._codeCompletionSuggestion.next(null);
  }

  getCodeCompletionStreamForCell(cellToken: string): Observable<CodeCompletionSuggestion> {
    return this._cellEditorTextChange.asObservable().pipe(
      withLatestFrom(this.notebookStateService.notebookType$),
      filter(([, notebookType]) => notebookType !== NotebookTypes.NotebookType.R),
      filter(() => this.cellEditorStateFacade.isCellSelected(cellToken)),
      debounceTime(CODE_COMPLETE_DEBOUNCE_TIME),
      this.buildCompletionContextOperator(cellToken),
      this.filterContextOperator(),
      this.fetchAndProcessSuggestionsOperator()
    );
  }

  /**
   * Following functions are used to build the code completion stream for a cell
   * 1. buildCompletionContextOperator: Builds the code completion context for the cell
   * 2. filterContextOperator: Filters the code completion context for the cell
   * 3. fetchAndProcessSuggestionsOperator: Fetches and processes the code completion suggestions for the cell
   */

  private buildCompletionContextOperator(cellToken: string): OperatorFunction<unknown, CodeCompletionContext> {
    return pipe(
      withLatestFrom(this.notebookStateService.pythonNotebookCells$),
      map(([, cells]) => {
        const editorContext = this.cellEditorStateFacade.getCellEditorContext();
        const codeCompletionContext = this.buildCodeCompletionContext(editorContext, cellToken, cells);
        return codeCompletionContext;
      })
    );
  }

  private filterContextOperator(): OperatorFunction<CodeCompletionContext, CodeCompletionContext> {
    return pipe(
      // TODO: if cursor at end of line or near whitespace or new closing bracket, we trigger completions, else not
      // For now, if there is text after cursor, we don't trigger completions
      filter((context) => this.isCursorAtEOL(context)),
      // don't trigger if we are in the middle of a keyword
      filter((context) => this.isInMiddleOfKeyword(context)),
      // trigger completion if cursor is at the start of a new cell
      filter((context) => this.isCursorAtStartOfNewCell(context))
    );
  }

  private fetchAndProcessSuggestionsOperator(): OperatorFunction<CodeCompletionContext, CodeCompletionSuggestion> {
    return pipe(
      switchMap((context) => {
        return this.fetchCodeCompletion(context.currentCodePrefix, context.currentCellSuffix).pipe(
          map((response) => {
            const suggestion: CodeCompletionSuggestion = {
              suggestion: response.renderedCode[0],
              cellPrefix: context.currentLinePrefix,
              cellSuffix: context.currentCellSuffix,
              suggestionId: response.requestId,
              suggestionCursorPosition: context.currentCursorPosition,
            };
            this._codeCompletionSuggestion.next(suggestion);
            return suggestion;
          })
        );
      })
    );
  }

  private buildCodeCompletionContext(
    context: EditorContext,
    selectedCellToken: string,
    pythonCells: [string, string][]
  ): CodeCompletionContext {
    const currentNotebookCode = pythonCells.map(([, cellContent]) => cellContent).join('\n\n');
    const previousCells = pythonCells.slice(
      0,
      pythonCells.findIndex(([cellToken, _]) => cellToken === selectedCellToken)
    );
    const previousCellsContent = previousCells.map(([, cellContent]) => cellContent).join('\n\n');
    const nextCells = pythonCells.slice(pythonCells.findIndex(([cellToken, _]) => cellToken === selectedCellToken) + 1);
    const nextCellsContent = nextCells.map(([, cellContent]) => cellContent).join('\n\n');

    const { textFromStartToCurrent, textFromCurrentToEnd } = context;

    const currentCodePrefix = [previousCellsContent, textFromStartToCurrent].join('\n\n');
    const currentCodeSuffix = [textFromCurrentToEnd, nextCellsContent].join('\n\n');
    const currentCellPrefix = textFromStartToCurrent;
    const currentCellSuffix = textFromCurrentToEnd;
    const currentLinePrefix = context.linePrefix;
    const currentLineSuffix = context.lineSuffix;

    const { tokenBeforeCursor, tokenAtCursor, tokenAfterCursor } = context;

    const codeCompletionContext: CodeCompletionContext = {
      currentCodeLine: context.codeLine,
      currentCodeLineAsArray: context.codeLineAsArray,
      currentCursorPosition: context.cursorPosition,
      currentCellToken: selectedCellToken,
      currentNotebookCode,
      currentNotebookCells: pythonCells,
      previousCells,
      nextCells,
      currentCodePrefix,
      currentCodeSuffix,
      currentCellPrefix,
      currentCellSuffix,
      tokenAtCursor: tokenAtCursor,
      tokenBeforeCursor: tokenBeforeCursor,
      tokenAfterCursor: tokenAfterCursor,
      currentLinePrefix: currentLinePrefix,
      currentLineSuffix: currentLineSuffix,
    };
    this._codeCompletionContext.next(codeCompletionContext);
    return codeCompletionContext;
  }

  /**
   * Helper functions to filter the code completion context for the cell
   */

  private isCursorAtEOL(context: CodeCompletionContext): boolean {
    return context.currentLineSuffix.length === 0;
  }

  private isInMiddleOfKeyword(context: CodeCompletionContext) {
    const { currentLinePrefix, currentLineSuffix, tokenAtCursor } = context;

    // Not in the middle of a keyword if we are at the beginning or end of a line
    if (currentLinePrefix === '' || currentLineSuffix === '') {
      return true;
    }

    const isInMiddleOfKeyword =
      (!currentLinePrefix.endsWith(' ') && !currentLineSuffix.startsWith(' ')) || tokenAtCursor?.type === 'string';

    return !isInMiddleOfKeyword;
  }

  private isCursorAtStartOfNewCell(context: CodeCompletionContext): boolean {
    const { currentCursorPosition } = context;
    if (currentCursorPosition.row !== 0) return true;
    return currentCursorPosition.column > 3;
  }
}
